import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { individualServices } from "@/data/servicesData";
import { useEffect } from "react";
import SEO, { breadcrumbSchema } from "@/components/SEO";

export default function ForIndividuals() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="individuals-page">
      <SEO
        title="Crypto Advisory for Individuals | Investment, Tax Planning, Portfolio Management"
        description="Expert crypto consultancy for individual investors in India. Services include crypto investment advisory, tax planning under Section 115BBH, portfolio management, financial planning, and digital asset security. 140+ clients advised."
        path="/individuals"
        keywords={[
          "crypto advisory for individuals",
          "crypto investment advisory India",
          "crypto tax consultant India",
          "Section 115BBH planning",
          "crypto portfolio management India",
          "digital asset security consultant",
          "NRI crypto tax advisory",
        ]}
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "For Individuals", path: "/individuals" },
        ])}
      />
      {/* Hero */}
      <section className="bg-[#21324D] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-end">
            <div className="lg:col-span-7">
              <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">For Individuals</p>
              <h1
                className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F2] leading-none tracking-tight mb-8"
                data-testid="individuals-heading"
              >
                Navigate crypto with a trusted guide by your side
              </h1>
              <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed max-w-xl">
                From your first crypto investment to comprehensive tax planning and portfolio management, BitGenix provides the expert advisory that helps you make confident, informed decisions at every stage.
              </p>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F5F5F2]/[0.06] border border-[#F5F5F2]/[0.08] p-5">
                  <p className="font-heading font-semibold text-3xl text-[#A7A55C]">140+</p>
                  <p className="font-body text-xs text-[#F5F5F2]/50 uppercase tracking-[0.1em] mt-1">Clients Advised</p>
                </div>
                <div className="bg-[#F5F5F2]/[0.06] border border-[#F5F5F2]/[0.08] p-5">
                  <p className="font-heading font-semibold text-3xl text-[#A7A55C]">$36M+</p>
                  <p className="font-body text-xs text-[#F5F5F2]/50 uppercase tracking-[0.1em] mt-1">Assets Advised</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-14">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Our Services for Individuals</p>
            <h2 className="font-heading font-semibold text-3xl text-[#21324D] tracking-tight leading-tight">
              Crypto advisory tailored to your personal goals
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {individualServices.map((service) => (
              <Link
                to={`/services/${service.slug}`}
                key={service.slug}
                className="group bg-white border border-[#383A46]/10 p-8 sm:p-10 rounded-none transition-all duration-300 hover:-translate-y-1 hover:border-[#A7A55C] hover:shadow-[0_16px_48px_-12px_rgba(33,50,77,0.12)]"
                data-testid={`individual-service-${service.slug}`}
              >
                <div className="bg-[#21324D] w-12 h-12 flex items-center justify-center mb-6">
                  <service.icon size={22} weight="duotone" className="text-[#A7A55C]" />
                </div>
                <h3 className="font-body font-medium text-lg text-[#21324D] mb-3 group-hover:text-[#A7A55C] transition-colors">
                  {service.title}
                </h3>
                <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-6">
                  {service.shortDesc}
                </p>
                <span className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">
                  Learn more <ArrowRight size={14} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ / SEO Section */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Why BitGenix</p>
              <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
                Why choose a crypto consultancy over doing it alone?
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="font-body text-base text-[#383A46] leading-[1.8] mb-6">
                The crypto market operates 24/7 with no circuit breakers, no investor protection guarantees, and constantly changing regulations. In India, you face a 30% flat tax on gains, 1% TDS on every transaction, and complex reporting requirements that most CAs are still learning to navigate.
              </p>
              <p className="font-body text-base text-[#383A46] leading-[1.8]">
                Working with a specialized crypto consultancy like BitGenix means having experienced professionals who understand both the technology and the regulatory landscape. We have helped over 140 clients across India and internationally, advising on more than $36 million in crypto assets. Our clients avoid costly mistakes, stay compliant, and make investment decisions with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#152F2C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#F5F5F2] tracking-tight leading-tight mb-6">
            Not sure where to start with crypto?
          </h2>
          <p className="font-body text-base text-[#F5F5F2]/65 max-w-xl mx-auto mb-10">
            Book a free discovery call. We will understand your situation and recommend the right path forward. No obligations.
          </p>
          <Link
            to="/contact"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-all duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2 hover:gap-3"
            data-testid="individuals-cta-button"
          >
            Book a Consultation <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
